import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { Fragment, useContext } from 'react';

import { CompetitionStatusEnum } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { SpecialButton } from '@flipster/react/design-system/special/button';
import { useCountdownDate } from '@flipster/react/design-utils/countdown-date';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { ImgOrVideo, contentfulImageUrl } from '../../contentful/assets';
import { authBunja } from '../../shared/auth/state';
import { BgmToggle } from './bgm-toggle';
import { RegisterButton } from './component/tc-button';
import { EventShareModal } from './modal/event-share-modal';
import {
  CompetitionContentfulContext,
  CompetitionIdContext,
  competitionApiBunja,
  competitionBunja,
  competitionTabsBunja,
} from './state';

export const Hero = () => {
  const { competitionContentful } = useBunja(competitionBunja);

  if (!competitionContentful) return null;

  return (
    <section className={css({ pos: 'relative', w: '100%', h: { base: 497, tablet: 572 } })}>
      <BgmToggle />
      <div
        className={css({
          pos: 'absolute',
          inset: 0,
          overflow: 'hidden',
        })}
      >
        <ImgOrVideo
          video={{
            base: competitionContentful.mobileHeroVideo?.url,
            tablet: competitionContentful.desktopHeroVideo?.url,
          }}
          img={{
            base: contentfulImageUrl(competitionContentful.mobileHeroPoster?.url ?? ''),
            tablet: contentfulImageUrl(competitionContentful.desktopHeroPoster?.url ?? ''),
          }}
          alt={competitionContentful.title}
          fetchPriority='high'
          css={{
            pos: 'absolute',
            inset: 0,
            w: '100%',
            h: '100%',
            objectFit: 'cover',
          }}
        />
        <div
          className={css({
            pos: 'absolute',
            inset: 0,
            _before: {
              content: '""',
              pos: 'absolute',
              top: 0,
              right: 0,
              w: '100%',
              h: 100,
              bg: 'linear-gradient(0deg, rgba(17, 17, 17, 0) 0%, rgba(17, 17, 17, 0.7) 70%, #111111 100%)',
              filter: 'blur(8px)',
              transform: 'scale(1.2)',
            },
            _after: {
              content: '""',
              pos: 'absolute',
              right: 0,
              bottom: 0,
              w: '100%',
              h: 100,
              bg: 'linear-gradient(180deg, rgba(17, 17, 17, 0) 0%, rgba(17, 17, 17, 0.7) 70%, #111111 100%)',
              filter: 'blur(6px)',
              transform: 'scale(1.2)',
            },
          })}
        />
        <div
          className={css({
            pos: 'absolute',
            inset: 0,
            _before: {
              content: '""',
              pos: 'absolute',
              top: 0,
              right: 0,
              w: '100%',
              h: 80,
              bg: 'linear-gradient(0deg, rgba(17, 17, 17, 0) 0%, #111111 100%)',
              filter: 'blur(3px)',
              transform: 'scale(1.2)',
            },
            _after: {
              content: '""',
              pos: 'absolute',
              right: 0,
              bottom: 0,
              w: '100%',
              h: 80,
              bg: 'linear-gradient(180deg, rgba(17, 17, 17, 0) 0%, #111111 100%)',
              filter: 'blur(3px)',
              transform: 'scale(1.2)',
            },
          })}
        />
      </div>
      <div
        className={center({
          zIndex: 1,
          pos: 'absolute',
          top: '50%',
          left: '50%',
          w: '100%',
          transform: 'translate(-50%, -50%)',
          px: { base: 24, tablet: 48 },
          textAlign: 'center',
          flexDirection: 'column',
          color: 'inverse.white',
          gap: 8,
        })}
      >
        <Linear />
        <p
          className={center({
            textStyle: 'Title/M',
            gap: 12,
          })}
        >
          {competitionContentful.subtitleIcon && (
            <img
              src={contentfulImageUrl(competitionContentful.subtitleIcon?.url ?? '')}
              alt={competitionContentful.subtitle ?? ''}
              height={24} // 높이만 고정
            />
          )}
          {competitionContentful.subtitle}
        </p>
        <h1
          className={css({
            textStyle: 'Display/XXL',
            textAlign: 'center',
            textWrap: 'balance',
            pt: 8,
          })}
        >
          {competitionContentful.title}
        </h1>
        <Linear />
        <Timer />
        <ButtonSection />
      </div>
    </section>
  );
};

const Linear = () => {
  return (
    <div
      role='separator'
      className={css({
        w: 1,
        h: 50,
        background: 'linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, #B4B4B4 50%, rgba(255, 255, 255, 0) 100%)',
      })}
    />
  );
};

const ButtonSection = () => {
  const id = useContext(CompetitionIdContext);
  const overlay = useOverlay();

  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const { isRegisteredAtom, competitionContentful } = useBunja(competitionBunja);
  const { isCookieLoggedInAtom } = useBunja(authBunja);
  const { scrollTargetRefAtom } = useBunja(competitionTabsBunja);
  const competitionStatus = useAtomValue(competitionStatusAtom);
  const isRegistered = useAtomValue(isRegisteredAtom);
  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const scrollTargetRef = useAtomValue(scrollTargetRefAtom);

  // 로그인 + "Register now" 버튼일 때만 스크롤이동
  // "Trade now" (로그인+등록완료+Ongoing)이거나 미로그인일 때는 스크롤 없이 바로 화면이동
  const shouldScrollOnClick =
    isCookieLoggedIn && !(competitionStatus === CompetitionStatusEnum.Ongoing && isRegistered);

  if (competitionStatus === CompetitionStatusEnum.Finished) return null;

  const handleShareClick = () => {
    segment.track(CompetitionsEvent.CompetitionTradingShareClick, { competition_id: id });
    overlay.open(() => (
      <CompetitionIdContext value={id}>
        <CompetitionContentfulContext value={competitionContentful}>
          <EventShareModal />
        </CompetitionContentfulContext>
      </CompetitionIdContext>
    ));
  };

  return (
    <div className={flex({ gap: 10, pt: 40 })}>
      <div
        onClick={() => {
          if (shouldScrollOnClick) {
            scrollTargetRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }}
      >
        <RegisterButton buttonLocation='top' />
      </div>
      <SpecialButton
        hierarchy='secondary'
        label={<FormattedMessage id='Common.r250401.Share' />}
        css={{ minW: 90 }}
        onClick={handleShareClick}
      />
    </div>
  );
};

const Timer = () => {
  const { competitionAtom } = useBunja(competitionBunja);
  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const competition = useAtomValue(competitionAtom);
  const [competitionStatus, setCompetitionStatus] = useAtom(competitionStatusAtom);

  if (!competition) return null;
  if (competitionStatus === CompetitionStatusEnum.Finished)
    return (
      <p
        className={css({
          textStyle: 'Title/XS',
          color: 'content.default.level-5',
          textAlign: 'center',
        })}
      >
        <FormattedMessage id='Competition.r251217.Stay_tuned_for_next_one' />
      </p>
    );

  const timerInfo = {
    [CompetitionStatusEnum.Upcoming]: {
      label: <FormattedMessage id='Competition.r251215.Event_starts_in' />,
      targetDate: competition.competitionStartTime,
      nextStatus: CompetitionStatusEnum.Ongoing,
    },
    [CompetitionStatusEnum.Ongoing]: {
      label: <FormattedMessage id='Competition.r251211.Event_ends_in' />,
      targetDate: competition.competitionEndTime,
      nextStatus: CompetitionStatusEnum.Finished,
    },
  }[competitionStatus as string];

  if (timerInfo) {
    return (
      <div className={vstack({ gap: 16 })}>
        <p
          className={css({
            textStyle: 'Title/XS',
            color: 'content.default.level-5',
            textAlign: 'center',
          })}
        >
          {timerInfo.label}
        </p>
        <CountdownDate targetDate={timerInfo.targetDate} onEnd={() => setCompetitionStatus(timerInfo.nextStatus)} />
      </div>
    );
  }

  return null;
};

interface CountdownProps {
  targetDate: string;
  onEnd: () => void;
}

const countdownList = ['dd', 'hh', 'mm', 'ss'] as const;

const CountdownDate = (props: CountdownProps) => {
  const isMounted = useMounted();
  const countdownData = useCountdownDate({
    maxUnit: countdownList[0],
    minUnit: countdownList.at(-1),
    ...props,
  });

  const rootClassName = hstack({
    textStyle: 'Title/M/Monospace',
    color: 'content.default.level-5',
    gap: 8,
    wordBreak: 'keep-all',
  });

  if (!isMounted) return <div className={rootClassName} translate='no' />;

  return (
    <div className={rootClassName} translate='no'>
      {countdownList.map((valueKey, index) => (
        <Fragment key={`${valueKey}_countdown`}>
          {index !== 0 && <span>:</span>}
          <div
            className={css({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 8,
              rounded: 8,
              bgColor: 'background.accent.secondary.tint',
              fontVariantNumeric: 'tabular-nums',
            })}
            style={{ backdropFilter: 'blur(10px)' }}
          >
            {String(countdownData?.[valueKey] ?? 0).padStart(2, '0')}
          </div>
        </Fragment>
      ))}
    </div>
  );
};
